/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Timestamp } from "firebase/firestore";
import { ActionContext } from "vuex";
import * as fb from "../../firebase";
//import router from "../../router";
import {
  ADD_MEMBER_TO_TEAM,
  CHANGE_TEAM_LEADER,
  doGetLeaderData,
  doGetTeamData,
  doRefreshMember,
  setLeaderUsername,
  SET_STATE_OWNER,
  CLEAR_STATE,
  UnilevelMember,
  UnilevelState,
  UnilevelTeam,
} from "./UnilevelInterfaces";

const initialState: UnilevelState = {
  ownerUsername: "",
  currentLeader: "",
  teamMap: new Map<string, UnilevelTeam>(),
};
const state: UnilevelState = { ...initialState };

const mutations = {
  ADD_MEMBER_TO_TEAM(
    state: UnilevelState,
    payload: {
      leader: UnilevelMember;
      side: "left" | "right";
      member: UnilevelMember;
    }
  ) {
    const teamLeaderNode = state.teamMap.get(payload.leader.usernamePK);
    if (teamLeaderNode != undefined) {
      const newTeam: UnilevelTeam = {
        leader: payload.member,
        leftTeam: new Array<UnilevelTeam>(),
        rightTeam: new Array<UnilevelTeam>(),
      };
      if (payload.side == "left") {
        const existingIdx = teamLeaderNode.leftTeam.findIndex(
          (u) => u.leader.usernamePK == payload.member.usernamePK
        );
        if (existingIdx === -1) {
          teamLeaderNode.leftTeam.push(newTeam);
        } else {
          teamLeaderNode.leftTeam[existingIdx].leader = payload.member;
        }
      } else if (payload.side == "right") {
        const existingIdx = teamLeaderNode.rightTeam.findIndex(
          (u) => u.leader.usernamePK == payload.member.usernamePK
        );
        if (existingIdx === -1) {
          teamLeaderNode.rightTeam.push(newTeam);
        } else {
          teamLeaderNode.rightTeam[existingIdx].leader = payload.member;
        }
      }
      if (
        state.teamMap.get(newTeam.leader.username.toLowerCase()) == undefined
      ) {
        state.teamMap.set(newTeam.leader.username.toLowerCase(), newTeam);
      }
    }
  },
  CHANGE_TEAM_LEADER(state: UnilevelState, newLeaderUsername: string) {
    state.currentLeader = newLeaderUsername;
  },
  SET_STATE_OWNER(state: UnilevelState, username: string) {
    state.ownerUsername = username || "";
  },
  /**
   * EL PARÁMETRO `state` NO ES DECORATIVO: es lo único que hace que este
   * borrado se vea.
   *
   * Vuex 4 guarda el estado dentro de un `reactive()` de Vue 3, es decir, un
   * Proxy. Los getters son `computed` y sólo se recalculan cuando la escritura
   * pasa POR ESE PROXY. Esta mutación se escribió sin parámetro, usando la
   * constante `state` del módulo —el objeto crudo, el que el Proxy envuelve—,
   * así que el valor cambiaba pero Vue nunca se enteraba: `getCurrentLeader`
   * seguía devolviendo, ya en la sesión del usuario siguiente, el líder del
   * usuario anterior, mientras `getTeam` (que sí lee el mapa en el momento de
   * la llamada) devolvía `undefined`. Resultado: unilevel en blanco tras
   * logout + login, hasta que el botón «ir a mi equipo» forzaba una mutación
   * bien hecha.
   */
  CLEAR_STATE(state: UnilevelState) {
    state.ownerUsername = initialState.ownerUsername;
    state.currentLeader = initialState.currentLeader;
    state.teamMap = new Map<string, UnilevelTeam>();
  },
};

function processTimestamps(memberData: any): UnilevelMember {
  for (const rankData in memberData.ranksCarrerData) {
    if (memberData.ranksCarrerData[rankData].date) {
      memberData.ranksCarrerData[rankData].date = new Timestamp(
        memberData.ranksCarrerData[rankData].date._seconds,
        memberData.ranksCarrerData[rankData].date._nanoseconds
      );
    }
  }
  memberData.subscriptions = new Map(Object.entries(memberData.subscriptions));
  if (memberData.scholarshipData != undefined) {
    memberData.scholarshipData.periodEndDate = new Timestamp(
      memberData.scholarshipData.periodEndDate._seconds,
      memberData.scholarshipData.periodEndDate._nanoseconds
    ).toDate();
  }
  return memberData as UnilevelMember;
}

const actions = {
  async initializeTeam(context: ActionContext<UnilevelState, any>) {
    const loggedUsername = context.rootState.auth.userProfileData._usernamePK;
    // SEGUNDA DEFENSA, independiente del logout: si lo que hay en memoria lo
    // dejó otro usuario, se tira entero antes de construir nada. Cubre también
    // las rutas de entrada que no pasen por `auth/logout` (un cambio de sesión
    // provocado desde fuera, un futuro «entrar como»...).
    if (
      context.state.ownerUsername &&
      context.state.ownerUsername !== loggedUsername
    ) {
      context.commit(CLEAR_STATE);
    }
    context.commit(SET_STATE_OWNER, loggedUsername);
    const team = context.getters.getTeam(loggedUsername);
    if (team == undefined) {
      await context.dispatch(doGetTeamData, { username: loggedUsername });
    }
    context.commit(CHANGE_TEAM_LEADER, loggedUsername);
  },

  doUpdateLeaderData(
    context: ActionContext<UnilevelState, any>,
    username: string
  ) {
    return context.dispatch(doGetTeamData, { username, forceUpdate: true });
  },

  // Nueva acción: 1 llamada al backend que devuelve líder + todo el equipo
  async [doGetTeamData](
    context: ActionContext<UnilevelState, any>,
    p: { username: string; forceUpdate?: boolean } = { username: "" }
  ) {
    const { username, forceUpdate = false } = p;
    const _usernamePK = username.toLowerCase();

    const team = context.getters.getTeam(_usernamePK);
    const members =
      (team?.leftTeam.length ?? 0) + (team?.rightTeam.length ?? 0);
    if (!forceUpdate && team != undefined && members > 0) {
      context.commit(CHANGE_TEAM_LEADER, _usernamePK);
      return;
    }

    const getTeamDataFn = fb.func.httpsCallable("Unilevel-getTeamData");
    const result = await getTeamDataFn({ username: _usernamePK }).then((r) => {
      if (r.data.OPCODE === "SUCCESS") return r.data.data;
      return undefined;
    });

    if (!result) {
      context.commit(CHANGE_TEAM_LEADER, "@@USER_NOT_FOUND@@");
      return;
    }

    const leader = processTimestamps(result.leader);

    // Registrar el nodo del líder en el mapa
    if (forceUpdate || !state.teamMap.has(leader.usernamePK)) {
      state.teamMap.set(leader.usernamePK, {
        leader,
        leftTeam: [],
        rightTeam: [],
      });
    } else {
      state.teamMap.get(leader.usernamePK)!.leader = leader;
      state.teamMap.get(leader.usernamePK)!.leftTeam = [];
      state.teamMap.get(leader.usernamePK)!.rightTeam = [];
    }

    for (const memberData of result.leftMembers) {
      context.commit(ADD_MEMBER_TO_TEAM, {
        leader,
        side: "left",
        member: processTimestamps(memberData),
      });
    }
    for (const memberData of result.rightMembers) {
      context.commit(ADD_MEMBER_TO_TEAM, {
        leader,
        side: "right",
        member: processTimestamps(memberData),
      });
    }

    context.commit(CHANGE_TEAM_LEADER, leader.usernamePK);
  },

  // Mantener getLeaderData para navegación legacy y compatibilidad
  async [doGetLeaderData](
    context: ActionContext<UnilevelState, any>,
    p: { username: string; forceUpdate?: boolean } = { username: "" }
  ) {
    return context.dispatch(doGetTeamData, p);
  },

  // Refresh de un solo miembro (botón en TeamMember)
  async [doRefreshMember](
    context: ActionContext<UnilevelState, any>,
    payload: {
      leader: UnilevelMember;
      member: UnilevelMember;
      side: "left" | "right";
    }
  ) {
    const getLeaderDataFn = fb.func.httpsCallable("Unilevel-getLeaderData");
    const result = await getLeaderDataFn({
      username: payload.member.usernamePK,
    }).then((r) => {
      if (r.data.OPCODE === "SUCCESS") return r.data.data;
      return undefined;
    });

    if (!result) return;

    context.commit(ADD_MEMBER_TO_TEAM, {
      leader: payload.leader,
      side: payload.side,
      member: processTimestamps(result),
    });
  },

  async [setLeaderUsername](
    context: ActionContext<UnilevelState, any>,
    newLeaderUsername: string
  ) {
    const loggedUsername = context.rootState.auth.userProfileData._usernamePK;
    newLeaderUsername =
      !newLeaderUsername || newLeaderUsername == undefined
        ? loggedUsername
        : newLeaderUsername.toLocaleLowerCase();
    await context.dispatch(doGetTeamData, { username: newLeaderUsername });
  },

  async searchUser(
    context: ActionContext<UnilevelState, any>,
    username: string
  ) {
    username = username.trim().toLowerCase();
    const team = context.getters.getTeam(username);
    if (team) {
      context.dispatch(setLeaderUsername, username);
    } else {
      await context.dispatch(doGetTeamData, { username });
    }
  },
};

const getters = {
  getTeam(state: UnilevelState, getters: any, rootState: any) {
    return (username?: string): UnilevelTeam | undefined => {
      username =
        username?.toLowerCase() || rootState.auth.userProfileData._usernamePK;
      if (username) {
        const team = state.teamMap.get(username);
        if (team) {
          return team;
        }
      }
    };
  },
  getCurrentTeam(state: UnilevelState): UnilevelTeam | undefined {
    return state.teamMap.get(state.currentLeader);
  },
  getCurrentLeader(state: UnilevelState): string | undefined {
    return state.currentLeader;
  },
  getStateOwner(state: UnilevelState): string {
    return state.ownerUsername;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
